import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Collapse,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Folder as FolderIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import {
  getInstanceManagementNamespaces,
  createInstanceManagementNamespace,
  updateInstanceManagementNamespace,
  deleteInstanceManagementNamespace,
  createInstanceManagementService,
  updateInstanceManagementService,
  deleteInstanceManagementService,
  fetchInstanceManagementServiceCBV,
  exportInstanceManagementData,
  importInstanceManagementData
} from '../../services/apiService';
import { get, authGet } from '../../services/apiService';
import { canPerformQAActions } from '../../services/authService';

/** Extract Azure service name from base URL (e.g. host qafet1dealcloserintegration1zus1.zus1.highqsolutions.com → qafet1dealcloserintegration1zus1) */
const getAzureServiceNameFromBaseUrl = (baseURL) => {
  if (!baseURL || !String(baseURL).trim()) return '';
  try {
    const url = new URL(baseURL.startsWith('http') ? baseURL : `http://${baseURL}`);
    const firstSegment = url.hostname.split('.')[0];
    return firstSegment || '';
  } catch {
    return '';
  }
};

/** Build getCBV.action URL from base URL */
const getCBVActionUrl = (baseURL) => {
  if (!baseURL || !String(baseURL).trim()) return '';
  const base = baseURL.trim().replace(/\/$/, '');
  return `${base}/getCBV.action`;
};

/**
 * Instance Management view: tree of Namespaces (parent) and Services (children).
 * Each service shows Azure service name (link to getCBV), release version and branch from getCBV.action.
 */
const InstanceManagementView = () => {
  const [tree, setTree] = useState([]);
  const [repositories, setRepositories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [expandedNamespaces, setExpandedNamespaces] = useState(new Set());
  const [fetchingServiceId, setFetchingServiceId] = useState(null);

  // Namespace dialog
  const [namespaceDialogOpen, setNamespaceDialogOpen] = useState(false);
  const [editingNamespace, setEditingNamespace] = useState(null);
  const [namespaceForm, setNamespaceForm] = useState({ name: '', description: '' });

  // Service dialog
  const [serviceDialogOpen, setServiceDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceNamespaceId, setServiceNamespaceId] = useState(null);

  const [serviceForm, setServiceForm] = useState({
    baseURL: '',
    repositoryId: '',
    displayName: '',
    adoPipelineId: '',
    adoPipelineUrl: '',
    adoServiceName: ''
  });

  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);

  // Currently deploying (refreshed every 15 sec)
  const [activeDeployments, setActiveDeployments] = useState([]);
  const fileInputRef = React.useRef(null);

  const canEdit = canPerformQAActions();

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [nsRes, repos] = await Promise.all([
        getInstanceManagementNamespaces(),
        get('/api/repositories')
      ]);
      const data = (nsRes && nsRes.data) ? nsRes.data : (Array.isArray(nsRes) ? nsRes : []);
      setTree(Array.isArray(data) ? data : []);
      setRepositories(Array.isArray(repos) ? repos : (repos?.data || []));
      if (expandedNamespaces.size === 0 && (Array.isArray(data) ? data : []).length > 0) {
        setExpandedNamespaces(new Set((Array.isArray(data) ? data : []).map(n => String(n._id))));
      }
    } catch (err) {
      console.error('Error fetching instance management data:', err);
      setError(err.message || 'Failed to load namespaces and services');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const loadActiveDeployments = async () => {
    try {
      const res = await authGet('/api/service-deployments/active');
      const list = (res && res.deployments) ? res.deployments : [];
      setActiveDeployments(Array.isArray(list) ? list : []);
    } catch (e) {
      setActiveDeployments([]);
    }
  };

  useEffect(() => {
    loadActiveDeployments();
    const interval = setInterval(loadActiveDeployments, 15000);
    return () => clearInterval(interval);
  }, []);

  const toggleNamespace = (id) => {
    const next = new Set(expandedNamespaces);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpandedNamespaces(next);
  };

  const expandAll = () => {
    setExpandedNamespaces(new Set(tree.map(n => String(n._id))));
  };

  const collapseAll = () => {
    setExpandedNamespaces(new Set());
  };

  const handleRefreshService = async (serviceId) => {
    try {
      setFetchingServiceId(serviceId);
      await fetchInstanceManagementServiceCBV(serviceId);
      setSuccessMessage('CBV data refreshed');
      setTimeout(() => setSuccessMessage(null), 3000);
      await fetchData();
    } catch (err) {
      setError(err.message || 'Failed to fetch CBV');
      setTimeout(() => setError(null), 5000);
    } finally {
      setFetchingServiceId(null);
    }
  };

  const handleRefreshNamespace = async (namespace) => {
    const services = namespace.services || [];
    for (const svc of services) {
      try {
        setFetchingServiceId(svc._id);
        await fetchInstanceManagementServiceCBV(svc._id);
      } catch (e) {
        console.warn('Refresh failed for service', svc._id, e);
      }
    }
    setFetchingServiceId(null);
    setSuccessMessage(`Refreshed ${services.length} service(s)`);
    setTimeout(() => setSuccessMessage(null), 3000);
    await fetchData();
  };

  const openAddNamespace = () => {
    setEditingNamespace(null);
    setNamespaceForm({ name: '', description: '' });
    setNamespaceDialogOpen(true);
  };

  const openEditNamespace = (ns) => {
    setEditingNamespace(ns);
    setNamespaceForm({ name: ns.name || '', description: ns.description || '' });
    setNamespaceDialogOpen(true);
  };

  const handleSaveNamespace = async () => {
    try {
      if (editingNamespace) {
        await updateInstanceManagementNamespace(editingNamespace._id, namespaceForm);
        setSuccessMessage('Namespace updated');
      } else {
        await createInstanceManagementNamespace(namespaceForm);
        setSuccessMessage('Namespace created');
      }
      setNamespaceDialogOpen(false);
      setTimeout(() => setSuccessMessage(null), 3000);
      await fetchData();
    } catch (err) {
      setError(err.message || 'Failed to save namespace');
    }
  };

  const handleDeleteNamespace = async (ns) => {
    if (!window.confirm(`Delete namespace "${ns.name}" and all its services?`)) return;
    try {
      await deleteInstanceManagementNamespace(ns._id);
      setSuccessMessage('Namespace deleted');
      setTimeout(() => setSuccessMessage(null), 3000);
      await fetchData();
    } catch (err) {
      setError(err.message || 'Failed to delete namespace');
    }
  };

  const openAddService = (namespaceId) => {
    setEditingService(null);
    setServiceNamespaceId(namespaceId);
    setServiceForm({
      baseURL: '',
      repositoryId: '',
      displayName: '',
      adoPipelineId: '',
      adoPipelineUrl: '',
      adoServiceName: ''
    });
    setServiceDialogOpen(true);
  };

  const openEditService = (svc) => {
    setEditingService(svc);
    setServiceNamespaceId(svc.namespaceId);
    setServiceForm({
      baseURL: svc.baseURL || '',
      repositoryId: svc.repositoryId?._id || svc.repositoryId || '',
      displayName: svc.displayName || '',
      adoPipelineId: svc.adoConfig?.pipelineId || '',
      adoPipelineUrl: svc.adoConfig?.pipelineUrl || '',
      adoServiceName: svc.adoConfig?.serviceName || ''
    });
    setServiceDialogOpen(true);
  };

  const handleSaveService = async () => {
    try {
      const serviceData = {
        baseURL: serviceForm.baseURL,
        repositoryId: serviceForm.repositoryId,
        displayName: serviceForm.displayName,
        adoConfig: {
          pipelineId: serviceForm.adoPipelineId ? Number(serviceForm.adoPipelineId) : undefined,
          pipelineUrl: serviceForm.adoPipelineUrl || undefined,
          serviceName: serviceForm.adoServiceName || undefined
        }
      };

      if (editingService) {
        await updateInstanceManagementService(editingService._id, serviceData);
        setSuccessMessage('Service updated');
      } else {
        await createInstanceManagementService(serviceNamespaceId, serviceData);
        setSuccessMessage('Service created');
      }
      setServiceDialogOpen(false);
      setTimeout(() => setSuccessMessage(null), 3000);
      await fetchData();
    } catch (err) {
      setError(err.message || 'Failed to save service');
    }
  };

  const handleDeleteService = async (svc) => {
    if (!window.confirm('Delete this service?')) return;
    try {
      await deleteInstanceManagementService(svc._id);
      setSuccessMessage('Service deleted');
      setTimeout(() => setSuccessMessage(null), 3000);
      await fetchData();
    } catch (err) {
      setError(err.message || 'Failed to delete service');
    }
  };

  const handleExportJson = async () => {
    try {
      setExporting(true);
      setError(null);
      const res = await exportInstanceManagementData();
      const data = res?.data ?? res;
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `instance-management-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      setSuccessMessage('Export downloaded');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err.message || 'Failed to export');
    } finally {
      setExporting(false);
    }
  };

  const handleUploadJson = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;
    try {
      setImporting(true);
      setError(null);
      const text = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsText(file);
      });
      const data = JSON.parse(text);
      const res = await importInstanceManagementData(data);
      const result = res?.data ?? res;
      const msg = result?.message ?? 'Import completed';
      const details = [
        result.namespacesCreated > 0 && `${result.namespacesCreated} namespace(s) created`,
        result.namespacesUpdated > 0 && `${result.namespacesUpdated} namespace(s) updated`,
        result.servicesCreated > 0 && `${result.servicesCreated} service(s) created`,
        result.servicesUpdated > 0 && `${result.servicesUpdated} service(s) updated`
      ].filter(Boolean).join('; ');
      setSuccessMessage(details ? `${msg}: ${details}` : msg);
      if (Array.isArray(result.errors) && result.errors.length > 0) {
        setError(result.errors.slice(0, 5).join('. ') + (result.errors.length > 5 ? ' …' : ''));
      }
      setTimeout(() => { setSuccessMessage(null); setError(null); }, 5000);
      await fetchData();
    } catch (err) {
      setError(err.message || 'Failed to import. Check that the file is valid JSON.');
    } finally {
      setImporting(false);
      event.target.value = '';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress size={40} />
      </Box>
    );
  }

  return (
    <Box>
      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
      {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}

      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button variant="outlined" size="small" onClick={expandAll}>Expand all</Button>
          <Button variant="outlined" size="small" onClick={collapseAll}>Collapse all</Button>
          {canEdit && (
            <>
              <Button variant="contained" startIcon={<AddIcon />} onClick={openAddNamespace}>
                Add Namespace
              </Button>
              <Tooltip title="Download all namespaces and services as JSON. Edit and upload to add or update records.">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}
                  onClick={handleExportJson}
                  disabled={exporting}
                >
                  Export JSON
                </Button>
              </Tooltip>
              <Tooltip title="Upload a previously exported JSON file to add or update namespaces and services.">
                <Button
                  variant="outlined"
                  size="small"
                  component="label"
                  startIcon={importing ? <CircularProgress size={16} /> : <UploadIcon />}
                  disabled={importing}
                >
                  Upload JSON
                  <input
                    type="file"
                    accept=".json,application/json"
                    hidden
                    ref={fileInputRef}
                    onChange={(e) => {
                      handleUploadJson(e);
                      e.target.value = '';
                    }}
                  />
                </Button>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>

      {/* Currently deploying - refreshes every 15 sec */}
      <Paper variant="outlined" sx={{ mb: 3, overflow: 'visible' }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ p: 2, pb: 1 }}>
          Currently deploying
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Namespace / Env</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Service</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Version / Branch</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {activeDeployments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} sx={{ color: 'text.secondary', fontStyle: 'italic', py: 2 }}>
                    No deployments in progress
                  </TableCell>
                </TableRow>
              ) : (
                activeDeployments.map((d) => (
                  <TableRow key={d._id} hover>
                    <TableCell>
                      {d.namespaceId?.name || d.adoPipeline?.environment || '—'}
                    </TableCell>
                    <TableCell>
                      {d.serviceId?.displayName || d.serviceId?.baseURL || '—'}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                      {d.githubWorkflow?.tagName || '—'}
                      {d.githubWorkflow?.branch ? ` (${d.githubWorkflow.branch})` : ''}
                    </TableCell>
                    <TableCell>
                      {d.status === 'pending' && 'Pending'}
                      {d.status === 'github_running' && 'GitHub running'}
                      {d.status === 'ado_running' && 'ADO pipeline running'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper variant="outlined" sx={{ overflow: 'visible', pr: 2 }}>
        <List disablePadding>
          {tree.length === 0 && (
            <ListItem>
              <ListItemText
                primary="No namespaces yet"
                secondary={canEdit ? 'Click "Add Namespace" to create one, then add services with a base URL and repository.' : ''}
              />
            </ListItem>
          )}
          {tree.map((ns) => {
            const nsId = String(ns._id);
            const isExpanded = expandedNamespaces.has(nsId);
            const services = ns.services || [];
            const hasServices = services.length > 0;

            return (
              <React.Fragment key={nsId}>
                <ListItem
                  disablePadding
                  sx={{
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    bgcolor: isExpanded ? 'action.hover' : undefined
                  }}
                >
                  <ListItemButton
                    onClick={() => toggleNamespace(nsId)}
                    sx={{ py: 1 }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', flexWrap: 'wrap', gap: 1, pr: 1 }}>
                      {hasServices ? (
                        isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />
                      ) : (
                        <Box sx={{ width: 24 }} />
                      )}
                      <FolderIcon sx={{ color: 'primary.main', mr: 1 }} />
                      <ListItemText
                        primary={
                          <Typography variant="subtitle1" fontWeight={600}>
                            {ns.name}
                          </Typography>
                        }
                        secondary={ns.description || `${services.length} service(s)`}
                        sx={{ flex: '1 1 auto', minWidth: 0 }}
                      />
                      {canEdit && (
                        <Box onClick={(e) => e.stopPropagation()} display="flex" gap={0.5} flexShrink={0}>
                          <Tooltip title="Add service">
                            <IconButton size="small" onClick={() => openAddService(nsId)}>
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit namespace">
                            <IconButton size="small" onClick={() => openEditNamespace(ns)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete namespace">
                            <IconButton size="small" color="error" onClick={() => handleDeleteNamespace(ns)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {hasServices && (
                            <Tooltip title="Refresh all services in this namespace">
                              <IconButton size="small" onClick={() => handleRefreshNamespace(ns)}>
                                <RefreshIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      )}
                    </Box>
                  </ListItemButton>
                </ListItem>
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <TableContainer component={Box} sx={{ pl: 4, pr: 3, pb: 1, bgcolor: 'background.default', overflow: 'visible' }}>
                    <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, width: '16%' }}>Service Name</TableCell>
                          <TableCell sx={{ fontWeight: 600, width: '38%' }}>Azure Service Name</TableCell>
                          <TableCell sx={{ fontWeight: 600, width: '12%' }}>Release</TableCell>
                          <TableCell sx={{ fontWeight: 600, width: '18%' }}>Branch / Commit</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, width: '16%' }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {services.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={5} sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                              {canEdit ? 'No services. Click "Add service" on the namespace row.' : 'No services.'}
                            </TableCell>
                          </TableRow>
                        )}
                        {services.map((svc) => {
                          const cbv = svc.cbvData || {};
                          const isFetching = fetchingServiceId === svc._id;
                          const serviceName = svc.displayName || cbv.applicationName || svc.baseURL || '—';
                          const repo = svc.repositoryId;
                          const org = 'tr';
                          const repoName = repo?.githubRepoName;
                          const fullRepoName = repoName ? (repoName.includes('/') ? repoName : `${org}/${repoName}`) : null;
                          const commitSha = (cbv.commit && /^[a-fA-F0-9]{7,40}$/.test(String(cbv.commit).trim()))
                            ? String(cbv.commit).trim()
                            : ((cbv.branch && /^[a-fA-F0-9]{7,40}$/.test(String(cbv.branch).trim())) ? String(cbv.branch).trim() : null);
                          const commitUrl = commitSha && fullRepoName ? `https://github.com/${fullRepoName}/commit/${commitSha}` : null;
                          const shortHash = commitSha ? (commitSha.length > 7 ? commitSha.slice(0, 7) : commitSha) : null;
                          const branchLabel = cbv.branch && !/^[a-fA-F0-9]{7,40}$/i.test(String(cbv.branch).trim()) ? String(cbv.branch).trim() : null;
                          return (
                            <TableRow key={svc._id} hover>
                              <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 0 }} title={serviceName}>{serviceName}</TableCell>
                              <TableCell sx={{ wordBreak: 'break-all', overflowWrap: 'break-word', maxWidth: 0 }} title={svc.baseURL || ''}>
                                {svc.baseURL ? (
                                  (() => {
                                    const label = getAzureServiceNameFromBaseUrl(svc.baseURL) || svc.baseURL;
                                    const href = getCBVActionUrl(svc.baseURL);
                                    return href ? (
                                      <Link href={href} target="_blank" rel="noopener noreferrer" sx={{ wordBreak: 'break-all' }}>
                                        {label}
                                      </Link>
                                    ) : (
                                      <span>{label}</span>
                                    );
                                  })()
                                ) : (
                                  '—'
                                )}
                              </TableCell>
                              <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 0 }}>{cbv.version || '—'}</TableCell>
                              <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 0 }}>
                                {commitUrl ? (
                                  <span style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                    {branchLabel ? `${branchLabel} (` : ''}
                                    <Link href={commitUrl} target="_blank" rel="noopener noreferrer">{shortHash}</Link>
                                    {branchLabel ? ')' : ''}
                                  </span>
                                ) : (
                                  (branchLabel || cbv.branch || '—')
                                )}
                              </TableCell>
                              <TableCell align="right" sx={{ whiteSpace: 'nowrap', pr: 2, width: '16%' }}>
                                <Tooltip title="Refresh release and branch from getCBV.action">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRefreshService(svc._id)}
                                    disabled={isFetching}
                                  >
                                    {isFetching ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
                                  </IconButton>
                                </Tooltip>
                                {canEdit && (
                                  <>
                                    <IconButton size="small" onClick={() => openEditService(svc)}>
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton size="small" color="error" onClick={() => handleDeleteService(svc)}>
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Collapse>
              </React.Fragment>
            );
          })}
        </List>
      </Paper>

      <Dialog open={namespaceDialogOpen} onClose={() => setNamespaceDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingNamespace ? 'Edit Namespace' : 'Add Namespace'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            fullWidth
            value={namespaceForm.name}
            onChange={(e) => setNamespaceForm(f => ({ ...f, name: e.target.value }))}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            value={namespaceForm.description}
            onChange={(e) => setNamespaceForm(f => ({ ...f, description: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNamespaceDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveNamespace} variant="contained" disabled={!namespaceForm.name.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={serviceDialogOpen} onClose={() => setServiceDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingService ? 'Edit Service' : 'Add Service'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Base URL"
            fullWidth
            placeholder="https://host/context"
            value={serviceForm.baseURL}
            onChange={(e) => setServiceForm(f => ({ ...f, baseURL: e.target.value }))}
            helperText="Base URL only; getCBV.action is appended by the server."
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Repository</InputLabel>
            <Select
              value={serviceForm.repositoryId}
              label="Repository"
              onChange={(e) => setServiceForm(f => ({ ...f, repositoryId: e.target.value }))}
            >
              {repositories.map((r) => (
                <MenuItem key={r._id} value={r._id}>{r.githubRepoName || r.sonarProjectName || r._id}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label="Display name (optional)"
            fullWidth
            value={serviceForm.displayName}
            onChange={(e) => setServiceForm(f => ({ ...f, displayName: e.target.value }))}
          />
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
            Azure DevOps Configuration (for deployment)
          </Typography>
          <TextField
            margin="dense"
            label="ADO Pipeline ID"
            fullWidth
            type="number"
            value={serviceForm.adoPipelineId}
            onChange={(e) => setServiceForm(f => ({ ...f, adoPipelineId: e.target.value }))}
            helperText="e.g., 2048"
          />
          <TextField
            margin="dense"
            label="ADO Pipeline URL (optional)"
            fullWidth
            value={serviceForm.adoPipelineUrl}
            onChange={(e) => setServiceForm(f => ({ ...f, adoPipelineUrl: e.target.value }))}
            helperText="Full pipeline URL"
          />
          <TextField
            margin="dense"
            label="ADO Service Name"
            fullWidth
            value={serviceForm.adoServiceName}
            onChange={(e) => setServiceForm(f => ({ ...f, adoServiceName: e.target.value }))}
            helperText="e.g., dashboard-builder-service"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setServiceDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveService}
            variant="contained"
            disabled={!serviceForm.baseURL.trim() || !serviceForm.repositoryId}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default InstanceManagementView;
